/**
 * Formats a date string (YYYY-MM-DD or ISO datetime) as a localized date.
 * All dates without timezones are assumed to be Pacific Time.
 *
 * @param dateString - Date string in YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss format
 * @returns Formatted date string (e.g., "Dec 13, 25") or "N/A" if invalid
 */
export function formatLocalDate(dateString: string | undefined): string {
  if (!dateString) return "N/A";

  try {
    // Handle ISO datetime format by extracting just the date portion
    // substring(0, 10) handles both "T"-separated and space-separated timestamps
    const datePart = dateString.substring(0, 10);

    // Parse date-only strings as local dates (Pacific Time)
    // Splitting and creating a Date object ensures it's interpreted in local timezone
    const [year, month, day] = datePart.split("-").map(Number);

    // Validate the parsed values
    if (!year || !month || !day) {
      return "N/A";
    }

    // Create date in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day);

    // Validate the date is valid
    if (isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      year: "2-digit",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "N/A";
  }
}

/**
 * Formats an ISO 8601 timestamp as a localized date.
 * Handles timestamps with timezone information (e.g., "2025-12-12T18:58:52.999230+00:00").
 *
 * @param timestamp - ISO 8601 timestamp string
 * @returns Formatted date string (e.g., "Dec 12, 2025") or "N/A" if invalid
 */
export function formatTimestamp(timestamp: string | undefined): string {
  if (!timestamp) return "N/A";

  try {
    // JavaScript's Date constructor correctly handles ISO 8601 timestamps with timezone
    const date = new Date(timestamp);

    // Validate the date is valid
    if (isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "N/A";
  }
}

/**
 * Formats an ISO 8601 timestamp as a short date format (Month/Day).
 *
 * @param timestamp - ISO 8601 timestamp string
 * @returns Formatted date string (e.g., "Dec/21") or empty string if invalid
 */
export function formatShortDate(timestamp: string | undefined): string {
  if (!timestamp) return "";

  try {
    // Extract date portion to avoid timezone shifting (same pattern as formatLocalDate)
    const datePart = timestamp.split("T")[0];
    const [year, month, day] = datePart.split("-").map(Number);

    if (!year || !month || !day) return "";

    // Create date in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day);

    if (isNaN(date.getTime())) return "";

    const monthStr = date.toLocaleDateString("en-US", { month: "short" });
    return `${monthStr}/${day}`;
  } catch {
    return "";
  }
}

/**
 * Formats a note with a date prefix.
 * If both note and timestamp exist, returns "Dec/21 note text"
 * If only note exists, returns just the note
 * If no note exists, returns "N/A"
 *
 * @param note - The note text
 * @param timestamp - ISO 8601 timestamp string
 * @returns Formatted note with date prefix or "N/A"
 */
export function formatNoteWithDate(
  note: string | undefined,
  timestamp: string | undefined
): string {
  if (!note) return "N/A";

  const datePrefix = formatShortDate(timestamp);

  if (datePrefix) {
    return `${datePrefix} ${note}`;
  }

  return note;
}
